import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const jmBullionProducts = [
  {
    id: "1gram-goldnote",
    name: "1 Gram Goldnote",
    subtitle: "Double Eagle Sealed Bar",
    price: "$199.12",
    availability: "In Stock",
    images: {
      obverse: "/1g-goldnote-obverse.png",
      reverse: "/1g-double-eagle-reverse-new.png"
    },
    description: "The classic 1 Gram Goldnote featuring 99.99% pure gold sealed within a polymer banknote design. Each note includes secure QR authentication.",
    features: [
      "1 Gram 99.99% Pure Gold",
      "Sealed Bar Format",
      "QR Authentication",
      "Ready for Shipping"
    ],
    buyUrl: "https://www.jmbullion.com/1-gram-goldnote-bar/"
  },
  {
    id: "year-of-horse",
    name: "2026 Year of the Horse Goldnote",
    subtitle: "1 Gram Sealed Bar",
    price: "$199.83",
    availability: "Out of Stock",
    images: {
      obverse: "/year-of-horse-obverse.png",
      reverse: "/year-of-horse-reverse.png"
    },
    description: "Celebrate the Year of the Horse with this stunning limited edition Goldnote. Features intricate horse design with 99.99% pure gold.",
    features: [
      "1 Gram 99.99% Pure Gold",
      "Limited Edition Design",
      "Year of the Horse 2026",
      "Collector's Item"
    ],
    buyUrl: "https://www.jmbullion.com/2026-1-gram-the-year-of-the-horse-goldnote/"
  },
  {
    id: "electrum",
    name: "Electrum Limited Edition",
    subtitle: "2-Fiche Goldnote (Sealed Bars)",
    price: "$240.93",
    availability: "Out of Stock",
    images: {
      obverse: "/electrum-obverse.png",
      reverse: "/electrum-reverse.png"
    },
    description: "The exclusive Electrum Limited Edition features two fiches of precious metal in a stunning collector's format. A true piece of numismatic art.",
    features: [
      "2-Fiche Design",
      "Limited Edition",
      "Premium Collector's Item",
      "Sealed Bar Format"
    ],
    buyUrl: "https://www.jmbullion.com/electrum-limited-edition-2-fiche-goldnote/"
  }
];

const ProductCard = ({ product }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const isOutOfStock = product.availability === "Out of Stock";
  
  return (
    <div className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/40 rounded-xl overflow-hidden transition-all duration-500">
      {/* Image with Flip */}
      <div 
        className="relative aspect-[4/3] p-6 flex items-center justify-center bg-gradient-to-b from-[#0A0A0A] to-[#050505] cursor-pointer"
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
        style={{ perspective: '1000px' }}
      >
        <div 
          className="w-full h-full transition-transform duration-700"
          style={{ 
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
          }}
        >
          {/* Front - Obverse */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-4"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <img 
              src={product.images.obverse} 
              alt={`${product.name} - Obverse`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          {/* Back - Reverse */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-4"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <img 
              src={product.images.reverse} 
              alt={`${product.name} - Reverse`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
        
        {/* Availability Badge */}
        <div className={`absolute top-4 right-4 px-3 py-1.5 rounded text-[0.6rem] tracking-wider uppercase font-medium z-10 ${
          isOutOfStock 
            ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
            : 'bg-green-500/20 text-green-400 border border-green-500/30'
        }`}>
          {product.availability}
        </div>
        
        {/* Hover indicator */}
        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 text-[0.5rem] tracking-wider text-[#D4AF37]/70 uppercase rounded opacity-60 group-hover:opacity-0 transition-opacity z-10">
          Hover to flip
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <p className="text-[0.6rem] tracking-[0.2em] uppercase text-[#D4AF37]/60 mb-2">{product.subtitle}</p>
        <h3 className="text-xl text-white/90 mb-2" style={{ fontFamily: 'Cinzel, serif' }}>{product.name}</h3>
        <p className="text-2xl text-[#D4AF37] font-light mb-4">{product.price}</p>
        <p className="text-sm text-white/50 font-light leading-relaxed mb-4">{product.description}</p>
        
        {/* Features */}
        <ul className="space-y-1.5 mb-6">
          {product.features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-xs text-white/40">
              <span className="w-1 h-1 rounded-full bg-[#D4AF37]/50" />
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Buy Button */}
        <a 
          href={product.buyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <Button 
            className={`w-full py-5 text-xs tracking-[0.15em] uppercase rounded-lg flex items-center justify-center gap-2 ${
              isOutOfStock 
                ? 'bg-white/10 text-white/50 cursor-not-allowed' 
                : 'bg-[#D4AF37] text-[#050505] hover:bg-[#EEDC9A]'
            }`}
            disabled={isOutOfStock}
          >
            {isOutOfStock ? 'Notify When Available' : 'Buy at JM Bullion'}
            <ExternalLink size={14} />
          </Button>
        </a>
      </div>
    </div>
  );
};

export const JMBullion = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="jm-bullion-page">
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Retailer Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-full mb-8">
              <img src="https://flagcdn.com/w40/us.png" alt="USA" className="w-6 h-4 object-cover rounded-sm" />
              <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[#D4AF37]">Exclusive US Retailer</span>
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6">
              <span className="text-white/90">Available at</span>
              <br />
              <span className="gold-gradient">JM Bullion</span>
            </h1>
            <p className="text-base md:text-lg text-white/60 font-light leading-relaxed max-w-xl mx-auto mb-8">
              Shop Goldnote products at JM Bullion, America's trusted precious metals dealer. 
              Free shipping on orders over $499.
            </p>
            
            <a 
              href="https://www.jmbullion.com/search/?q=goldnote"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#050505] hover:bg-[#EEDC9A] text-xs tracking-[0.2em] uppercase font-medium rounded-lg transition-colors"
            >
              View All at JM Bullion
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4 text-center">
              Available Products
            </p>
            <h2 className="text-2xl md:text-3xl text-white/90 text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              Goldnotes at JM Bullion
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {jmBullionProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* About JM Bullion */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl text-white/90 mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
              About JM Bullion
            </h3>
            <p className="text-sm text-white/50 font-light leading-relaxed mb-6">
              JM Bullion is one of the nation's largest and most trusted online precious metals dealers. 
              With competitive pricing, fast shipping, and excellent customer service, they've served 
              hundreds of thousands of satisfied customers since 2011.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-white/40">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                BBB Accredited
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Free Shipping $499+
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Secure Checkout
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JMBullion;
